import React, { useEffect, useState } from "react";
import { X, Calendar, Filter, BarChart3, Boxes } from "lucide-react";
import api from "@/api";

/**
 * Phase 14 — Click-Units-Sold drilldown modal.
 * Hits GET /api/dashboard/units-breakdown
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   title: string                    e.g., "Units Sold"
 *   defaultGroupBy: "product"|"district"|"salesman"|"manager"
 *   filters: optional pre-applied filters {district_id, manager_id, salesman_id, shop_id}
 *   showGroupSwitcher: boolean (default true)
 */
export default function UnitsDrilldown({
  open, onClose, title = "Units Breakdown",
  defaultGroupBy = "product", filters = {}, showGroupSwitcher = true,
}) {
  const [scope, setScope] = useState("today");
  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { scope, group_by: groupBy, ...filters };
    if (scope === "custom") { params.start_date = start; params.end_date = end; }
    api.get("/dashboard/units-breakdown", { params })
      .then(r => setRows(r.data?.rows || []))
      .finally(() => setLoading(false));
  }, [open, scope, groupBy, start, end, JSON.stringify(filters)]);  // eslint-disable-line

  if (!open) return null;
  const total = rows.reduce((s, r) => s + (r.units || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="units-drilldown-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> Drilldown
            </div>
            <h2 className="heading-font text-xl font-bold text-[#1D1D1F]">{title}</h2>
          </div>
          <button onClick={onClose} data-testid="close-units-drilldown" className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 grid place-items-center"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 sm:px-6 py-3 border-b border-gray-100 space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-[#86868B]" />
            {[
              ["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setScope(k)}
                data-testid={`scope-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${scope === k ? "bg-[#0A1A3F] text-white shadow" : "bg-gray-100 text-[#1D1D1F] hover:bg-gray-200"}`}
              >{label}</button>
            ))}
          </div>
          {scope === "custom" && (
            <div className="flex items-center gap-2">
              <input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="custom-start" className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm" />
              <span className="text-[#86868B] text-sm">→</span>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="custom-end" className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm" />
            </div>
          )}
          {showGroupSwitcher && (
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-[#86868B]" />
              {[
                ["product", "By Product"], ["district", "By District"],
                ["salesman", "By Salesman"], ["manager", "By Manager"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setGroupBy(k)}
                  data-testid={`groupby-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${groupBy === k ? "bg-[#0055FF] text-white shadow" : "bg-gray-100 text-[#1D1D1F] hover:bg-gray-200"}`}
                >{label}</button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#5856D6]/5 to-transparent">
          <div className="text-sm text-[#86868B]">Total {groupBy} units</div>
          <div className="heading-font text-2xl font-bold text-[#5856D6] flex items-center gap-1.5">
            <Boxes className="w-5 h-5" /> {total.toLocaleString()}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 sm:p-4">
          {loading ? (
            <div className="text-center text-[#86868B] py-12">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-[#86868B] py-12">No data for selected period</div>
          ) : (
            <div className="space-y-2">
              {rows.map((r, i) => {
                const pct = total > 0 ? (r.units / total) * 100 : 0;
                return (
                  <div key={r.id || r.product_id || i} className="bg-[#F5F5F7] rounded-2xl p-3" data-testid={`drilldown-row-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-bold text-[#1D1D1F] truncate flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-[#5856D6] text-white text-xs grid place-items-center flex-shrink-0 heading-font font-bold">{i + 1}</div>
                        <span className="truncate">{r.name || r.name_en || "—"}</span>
                      </div>
                      <div className="heading-font text-lg font-bold text-[#1D1D1F] flex-shrink-0 ml-2">
                        {r.units?.toLocaleString() || 0}
                      </div>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#5856D6] to-[#0055FF] rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="text-[10px] text-[#86868B] mt-1 flex justify-between">
                      <span>{pct.toFixed(1)}% of total</span>
                      {r.orders !== undefined && <span>{r.orders} orders</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
